// /admin/ops/runs — scheduler monitoring. Lists recent recon_runs
// with task / status / trigger-kind filters, summary counts, cursor
// pagination, and per-row deep-links into Airflow.
//
// URL search is the single source of truth for filter state so a
// "show me failed balance runs from the last hour" view is shareable.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import { fetchRuns, opsKeys } from "@/lib/ops-queries";
import type { RunsListParams } from "@/lib/ops-queries";
import {
  parseTaskType,
  TASK_TYPE_OPTIONS,
  type RunsPage,
  type RunStatus,
  type RunSummary,
  type TaskType,
} from "@/lib/ops-types";

const AIRFLOW_BASE = "https://sit-sm-airflow.devcula.com";

type TriggerKind = "scheduled" | "manual" | "backfill";

interface Search {
  task_type?: TaskType;
  status?: RunStatus;
  trigger_kind?: TriggerKind;
}

export const Route = createFileRoute("/_app/ops/runs/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    task_type: parseTaskType(s.task_type),
    status: parseStatus(s.status),
    trigger_kind: parseTriggerKind(s.trigger_kind),
  }),
  component: RunsList,
});

function RunsList() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const baseParams = useMemo(
    () => ({
      task_type: search.task_type ? [search.task_type] : undefined,
      status: search.status ? [search.status] : undefined,
      trigger_kind: search.trigger_kind ? [search.trigger_kind] : undefined,
      limit: 50,
    }),
    [search.task_type, search.status, search.trigger_kind],
  );

  // Page 1 is the source of truth for refetchInterval; "Load more" is
  // a separate accumulator so polling on page 1 doesn't clobber the
  // already-loaded tail.
  const head = useQuery({
    queryKey: opsKeys.runs(baseParams),
    queryFn: () => fetchRuns(baseParams),
    refetchInterval: 15_000,
  });

  const filterKey = JSON.stringify(baseParams);
  const more = useNextPages(filterKey, baseParams);

  const items = useMemo(() => {
    const head1 = head.data?.Items ?? [];
    const tail = more.pages.flatMap((p) => p.Items);
    return [...head1, ...tail];
  }, [head.data, more.pages]);

  const counts = useMemo(() => summarize(items), [items]);
  const nextCursor =
    more.pages.length > 0
      ? more.pages[more.pages.length - 1].NextCursor
      : head.data?.NextCursor;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            value={search.task_type ?? ""}
            onChange={(e) =>
              navigate({
                search: (s) => ({
                  ...s,
                  task_type: (e.target.value as TaskType) || undefined,
                }),
              })
            }
            placeholder="Any task type"
            options={TASK_TYPE_OPTIONS}
          />
        </div>
        <div className="w-44">
          <Select
            value={search.status ?? ""}
            onChange={(e) =>
              navigate({
                search: (s) => ({
                  ...s,
                  status: (e.target.value as RunStatus) || undefined,
                }),
              })
            }
            placeholder="Any status"
            options={[
              { value: "running", label: "Running" },
              { value: "success", label: "Success" },
              { value: "failed", label: "Failed" },
              { value: "partial", label: "Partial" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
        </div>
        <div className="w-44">
          <Select
            value={search.trigger_kind ?? ""}
            onChange={(e) =>
              navigate({
                search: (s) => ({
                  ...s,
                  trigger_kind: (e.target.value as TriggerKind) || undefined,
                }),
              })
            }
            placeholder="Any trigger"
            options={[
              { value: "scheduled", label: "Scheduled" },
              { value: "manual", label: "Manual" },
              { value: "backfill", label: "Backfill" },
            ]}
          />
        </div>
        <div className="ml-auto text-xs text-muted">
          {items.length > 0 ? `${items.length} loaded` : ""}
        </div>
      </div>

      {/* Status mix across the loaded set. Mirrors what the operator
          can scroll to, not a global count — keeps the request shape
          simple. */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatChip label="Running" value={counts.running} tone="accent" />
          <StatChip label="Success" value={counts.success} tone="success" />
          <StatChip label="Failed" value={counts.failed} tone="danger" />
          <StatChip label="Partial" value={counts.partial} tone="warning" />
          <StatChip label="Cancelled" value={counts.cancelled} tone="muted" />
        </div>
      )}

      <Card>
        <CardBody className="p-0">
          {head.isLoading && <Skeleton className="m-5 h-32" />}
          {head.data && items.length === 0 && (
            <div className="p-8 text-center text-muted">
              No runs match the current filters.
            </div>
          )}
          {items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-2 py-2 text-left">Run</th>
                  <th className="px-2 py-2 text-left">DAG</th>
                  <th className="px-2 py-2 text-left">Type</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Trigger</th>
                  <th className="px-2 py-2 text-left">Started</th>
                  <th className="px-2 py-2 text-right">Findings new</th>
                  <th className="px-2 py-2 text-right">Recurring</th>
                  <th className="px-2 py-2 text-right">Duration</th>
                  <th className="px-2 py-2 text-right">Airflow</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <RunRow key={r.run_id} r={r} />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            disabled={more.loading}
            onClick={() => more.loadNext(nextCursor)}
          >
            {more.loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}

function RunRow({ r }: { r: RunSummary }) {
  const airflowURL = airflowRunURL(r.airflow_dag_id, r.airflow_run_id);
  return (
    <tr className="border-b border-border last:border-0 hover:bg-card">
      <td className="px-2 py-2">
        <Link
          to="/ops/runs/$id"
          params={{ id: r.run_id }}
          className="font-mono text-xs hover:underline"
        >
          #{r.run_id}
        </Link>
      </td>
      <td className="px-2 py-2 text-xs">{r.airflow_dag_id}</td>
      <td className="px-2 py-2">{r.task_type}</td>
      <td className="px-2 py-2">
        <Badge tone={statusTone(r.status)}>{r.status}</Badge>
      </td>
      <td className="px-2 py-2 text-xs">
        <span className="text-fg">{r.trigger_kind}</span>
        {r.triggered_by && (
          <span className="text-muted"> · {r.triggered_by}</span>
        )}
      </td>
      <td className="px-2 py-2 text-xs text-muted">
        {fmtRelative(new Date(r.started_at_unix_ms).toISOString())}
      </td>
      <td className="px-2 py-2 text-right">
        {r.findings_new > 0 ? (
          <Link
            to="/ops/findings"
            search={{ run_id: String(r.run_id), only_open: "true" }}
            className="font-semibold hover:underline"
          >
            {r.findings_new}
          </Link>
        ) : (
          r.findings_new
        )}
      </td>
      <td className="px-2 py-2 text-right text-muted">{r.findings_recurring}</td>
      <td className="px-2 py-2 text-right text-xs">
        {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : "—"}
      </td>
      <td className="px-2 py-2 text-right text-xs">
        <a
          className="text-muted underline hover:text-fg"
          href={airflowURL}
          target="_blank"
          rel="noreferrer"
          title="Open this run in Airflow"
        >
          ↗
        </a>
      </td>
    </tr>
  );
}

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "accent" | "success" | "danger" | "warning" | "muted";
}) {
  return (
    <Card>
      <CardBody className="flex items-baseline justify-between gap-3 py-3">
        <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
        <div className="flex items-center gap-2">
          <Badge tone={tone}>{toneGlyph(tone)}</Badge>
          <span className="font-display text-xl">{value}</span>
        </div>
      </CardBody>
    </Card>
  );
}

// ----- helpers -----

function airflowRunURL(dagID: string, runID: string): string {
  return `${AIRFLOW_BASE}/dags/${encodeURIComponent(dagID)}/grid?dag_run_id=${encodeURIComponent(runID)}`;
}

function parseStatus(v: unknown): RunStatus | undefined {
  if (
    v === "running" ||
    v === "success" ||
    v === "failed" ||
    v === "cancelled" ||
    v === "partial"
  ) {
    return v;
  }
  return undefined;
}

function parseTriggerKind(v: unknown): TriggerKind | undefined {
  if (v === "scheduled" || v === "manual" || v === "backfill") return v;
  return undefined;
}

function statusTone(
  s: string,
): "success" | "danger" | "warning" | "muted" | "accent" {
  switch (s) {
    case "success":
      return "success";
    case "failed":
      return "danger";
    case "partial":
      return "warning";
    case "running":
      return "accent";
    case "cancelled":
      return "muted";
    default:
      return "muted";
  }
}

function toneGlyph(tone: string): string {
  switch (tone) {
    case "accent":
      return "↻";
    case "success":
      return "✓";
    case "danger":
      return "!";
    case "warning":
      return "~";
    default:
      return "•";
  }
}

function summarize(items: RunSummary[]) {
  const c = {
    running: 0,
    success: 0,
    failed: 0,
    partial: 0,
    cancelled: 0,
  };
  for (const r of items) {
    if (r.status in c) {
      (c as Record<string, number>)[r.status] += 1;
    }
  }
  return c;
}

// useNextPages — local cursor accumulator for "Load more". Kept out
// of useInfiniteQuery because page 1 polls independently and the
// natural fit is "polled head + accumulated tail".
function useNextPages(resetKey: string, baseParams: RunsListParams) {
  const [pages, setPages] = useState<RunsPage[]>([]);
  const [loading, setLoading] = useState(false);
  const seenKey = useRef(resetKey);

  useEffect(() => {
    if (seenKey.current !== resetKey) {
      setPages([]);
      seenKey.current = resetKey;
    }
  }, [resetKey]);

  async function loadNext(cursor: string) {
    if (loading || !cursor) return;
    setLoading(true);
    try {
      const next = await fetchRuns({ ...baseParams, cursor });
      setPages((p) => [...p, next]);
    } finally {
      setLoading(false);
    }
  }

  return { pages, loading, loadNext };
}
